// Phase F2 — Supervisor chat UX (presentation-layer enhancement).
//
// When the L3 synthesis path emits an answer, citations are tagged with
// `[Expert: <name>]` per converged decision #7 (persona-leak prevention)
// and #8 (citation-strict synthesis). This component sniffs the message
// body for those tags and renders:
//   1. A chip per unique expert above the message body.
//   2. A "Conflict surfaced" warning badge when the synthesis prompt's
//      "compose, don't conclude" rule emitted both views (per the
//      "both views" / "Per [Expert: A] vs Per [Expert: B]" patterns
//      pinned by Phase B's L3 synthesis tests).
//
// Conservative regexes — false-positives are tolerable, but we MUST NOT
// mangle the message body. The regex test runs on the raw text; rendering
// happens on a sibling element (the body itself is untouched).
//
// Keeps changes minimal: just a passive enhancement layered on top of
// the existing chat surface.

import React from "react";
import { COLORS } from "../theme/colors";
import { Badge } from "./ui";

// `[Expert: Name Goes Here]` — name allows letters, digits, spaces,
// hyphens, ampersands, slashes, and parens to cover the phase-1
// templates' expert names (e.g. "Network Operations Expert",
// "Clause Analyst", "Legal & Compliance"). Stops at the first `]`.
const EXPERT_TAG_RX = /\[Expert:\s*([^\]]+?)\s*\]/g;

// L3 synthesis surfaces conflicts via the "both views" idiom or the
// `Per [Expert: A] ... vs Per [Expert: B]` shape. We don't want to
// false-positive on a single mention of "view" or "vs" in normal prose,
// so we require either of the two shapes explicitly.
const CONFLICT_PATTERNS = [
  /\bboth views\b/i,
  /Per \[Expert:[^\]]+\][\s\S]{0,200}\bvs\b[\s\S]{0,200}Per \[Expert:[^\]]+\]/,
  /Per \[Expert:[^\]]+\][\s\S]{0,80}\nPer \[Expert:[^\]]+\]/,
];

function extractExperts(text) {
  if (!text || typeof text !== "string") return [];
  const seen = new Set();
  const order = [];
  EXPERT_TAG_RX.lastIndex = 0;
  let m;
  while ((m = EXPERT_TAG_RX.exec(text)) !== null) {
    const name = (m[1] || "").trim();
    if (!name) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    order.push(name);
  }
  return order;
}

function detectConflict(text) {
  if (!text || typeof text !== "string") return false;
  return CONFLICT_PATTERNS.some((rx) => rx.test(text));
}

/**
 * Renders the expert chips + conflict badge. Returns null when the
 * message has no `[Expert: ...]` tags — so non-supervisor messages and
 * regular agent chat are completely untouched.
 */
export function SupervisorMessageDecorations({ text }) {
  const experts = extractExperts(text);
  if (experts.length === 0) return null;

  const conflict = detectConflict(text);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
      {experts.map((name) => (
        <Badge
          key={name}
          color={COLORS.cyan}
          style={{ textTransform: "none", letterSpacing: 0 }}
        >
          {"\u2605 "}Expert: {name}
        </Badge>
      ))}
      {conflict && (
        <Badge
          color={COLORS.orange}
          style={{ textTransform: "none", letterSpacing: 0 }}
        >
          {"\u26A0 "}Conflict surfaced
        </Badge>
      )}
    </div>
  );
}

export default SupervisorMessageDecorations;
